/**
 * Layout interne des blocs composites (Texte+Image, CTA, …) :
 * espacement (`gap`) et alignement transversal (`alignItems`) entre
 * sous-parties — distinct du Style Design (margin/padding du wrapper).
 *
 * Réutilise `LayoutGap` / `resolveGapLayout` / `layoutAlignItemsClass`
 * (même UX que Conteneur / Colonnes / Grille).
 */
import { cmsNew } from "@/lib/cms/cms-new-i18n";

import { resolveGapLayout } from "./gap";
import { cx } from "./internal";
import { layoutAlignItemsClass } from "./size";
import type { GapInlineStyle, LayoutAlign, LayoutGap } from "./types";

export type CompositeInnerGapProps = {
  gap?: LayoutGap | null;
  gapCustomPx?: number | null;
  gapRow?: LayoutGap | null;
  gapCol?: LayoutGap | null;
  gapRowCustomPx?: number | null;
  gapColCustomPx?: number | null;
};

export type CompositeInnerAlignProps = {
  /** Alignement transversal (grid/flex `align-items`). */
  alignItems?: LayoutAlign | null;
};

/** Absent / null → `none` (rétro-compat pages existantes `gap-0`). */
export function normalizeCompositeGap(gap?: LayoutGap | null): LayoutGap {
  return gap ?? "none";
}

export function normalizeCompositeAlignItems(
  align?: LayoutAlign | null,
  fallback: LayoutAlign = "center",
): LayoutAlign {
  return align ?? fallback;
}

/**
 * Classes + style inline pour le conteneur interne d'un composite
 * (grille / flex qui porte les sous-parties).
 */
export function resolveCompositeInnerLayout(
  props: CompositeInnerGapProps &
    CompositeInnerAlignProps & {
      /** Défaut d'alignement si `alignItems` absent (TextImage = center). */
      alignFallback?: LayoutAlign;
      /** Inclure ou non la classe align-items (ex. grille de cartes). */
      withAlignItems?: boolean;
    },
): { className: string; style: GapInlineStyle } {
  const gap = normalizeCompositeGap(props.gap);
  const { className: gapClass, style } = resolveGapLayout({
    gap,
    gapCustomPx: props.gapCustomPx,
    gapRow: props.gapRow,
    gapCol: props.gapCol,
    gapRowCustomPx: props.gapRowCustomPx,
    gapColCustomPx: props.gapColCustomPx,
  });
  const withAlign = props.withAlignItems !== false;
  const alignClass = withAlign
    ? layoutAlignItemsClass(
        normalizeCompositeAlignItems(
          props.alignItems,
          props.alignFallback ?? "center",
        ),
      )
    : "";
  return { className: cx(gapClass, alignClass), style };
}

/** Options select pour alignItems (libellés i18n). */
export function getCompositeAlignItemsOptions(): ReadonlyArray<{
  label: string;
  value: LayoutAlign;
}> {
  return [
    {
      label: cmsNew("cms.new.blocks.composite.alignItems.start", "Début"),
      value: "start",
    },
    {
      label: cmsNew("cms.new.blocks.composite.alignItems.center", "Centre"),
      value: "center",
    },
    {
      label: cmsNew("cms.new.blocks.composite.alignItems.end", "Fin"),
      value: "end",
    },
    {
      label: cmsNew("cms.new.blocks.composite.alignItems.stretch", "Étiré"),
      value: "stretch",
    },
  ];
}
